import request from '../request';
import { UserSession } from '../types';
import { AuthAPI } from './api-types';
import { NextScreen } from './types';
import urls from './urls';

async function getServer(params: AuthAPI.GetServerRequest): Promise<AuthAPI.GetServerResponse> {
  const { data } = await request.get<AuthAPI.GetServerResponse>(urls.getServer, {
    baseURL: process.env.REACT_APP_API_GATEWAY,
    params,
  });

  return data;
}

export async function getUserRegistration(
  payload: AuthAPI.GetUserRegistrationRequest
): Promise<AuthAPI.GetUserRegistrationTransformedResponse> {
  const getServerResponse = await getServer(payload);
  const { next, cluster } = getServerResponse;

  if (next === NextScreen.SSO) {
    const { next, authUrl, sessionId } = getServerResponse;
    return { next, authUrl, sessionId };
  }

  if (next !== NextScreen.CHANGE_SERVER) return { next };

  const { data } = await request.post<AuthAPI.GetUserRegistrationResponse>(
    urls.getUserRegistration,
    payload,
    {
      baseURL: cluster,
    }
  );

  const sessionBase: AuthAPI.GetUserRegistrationBaseTransformedResponse = {
    cluster,
    userExists: true as const,
    userId: data.userId,
    name: data.name,
    email: payload.emailId,
    s3BucketURL: data.avatarURL,
    avatar: data.avatar,
    university: {
      name: data.university.univName,
      slug: data.university.univSlug,
    },
  };

  if (data.next === NextScreen.GET_PASSWORD) {
    const session = {
      ...sessionBase,
      verified: true as const,
      canCreateCourses: 1,
      profileCompleted: true as const,
    };

    return { session, next: data.next };
  }

  const session = {
    ...sessionBase,
    verified: Boolean(data.verified),
    canCreateCourses: data.canCreateCourses,
    profileCompleted: Boolean(data.profileCompleted),
  };

  return { session, next: data.next };
}

export async function getSsoStatus(params: AuthAPI.GetSsoStatusRequest) {
  const { data } = await request.get<AuthAPI.GetSsoStatusResponse>(urls.ssoStatus(params.sessionId), {
    baseURL: process.env.REACT_APP_API_GATEWAY,
  });
  return data;
}

export async function login(payload: AuthAPI.LoginRequest): Promise<AuthAPI.LoginTransformedResponse> {
  const { data } = await request.post<AuthAPI.LoginResponse>(urls.login, payload, {
    validateStatus: (status) => status >= 200 && status < 500,
  });

  if ('error' in data) {
    if (data.statusCode === 409) {
      return { isSuccess: false, message: data.message };
    }
  }

  if ('next' in data) {
    const { next } = data;
    if (next === NextScreen.PPT_PLUGIN_NOT_ACCESSIBLE) {
      const { errorMessage } = data;
      return { isSuccess: false, message: errorMessage, next };
    }

    const { socket, token } = data;

    if (next === NextScreen.LANDING_PAGE) {
      return {
        isSuccess: true,
        next,
        socket,
        token,
      };
    }
  }

  return {
    isSuccess: false,
    // TODO: translate it.
    message: 'something went wrong!',
  };
}

export async function ssoLogin(payload: AuthAPI.SsoLoginRequest) {
  const { baseURL, ...rest } = payload;
  const _payload = {
    ...rest,
    agent: 'web',
  };

  const { data } = await request.post<AuthAPI.SsoLoginResponse>(urls.ssoLogin, _payload, {
    baseURL,
  });

  const {
    userId,
    emailId,
    name,
    avatar,
    avatarURL,
    university,
    profileCompleted,
    canCreateCourses,
    socket,
    token,
    next,
  } = data;

  const session: UserSession = {
    userId: userId,
    name: name,
    email: emailId,
    avatar: avatar,
    cluster: baseURL,
    s3BucketURL: avatarURL,
    userExists: true as const,
    canCreateCourses,
    profileCompleted: Boolean(profileCompleted),
    university: {
      name: university.univName,
      slug: university.univSlug,
    },
    token,
    socket,
    verified: true as const,
  };

  return { next, session };
}

export async function logout() {
  await request.get(urls.logout);
}
